// Lane API
import queryString from 'query-string'
import {
    RequestConfigFunction, SelectorConfig,
} from 'app/types/request.types'

import {
    CollectionServiceType,
    DefaultTemperatureRange, DeliveryServiceType, Frequency,
    LaneStatus, LeaseType, TemperatureRange,
} from 'app/enums/label.enums'
import {
    LaneBulkCreatePayload,
} from 'app/Apps/AccountManagement/AccountLanes/AccountLanes.types'
import {
    LaneSelector,
} from 'app/Apps/Pricing/Pricing.types'
import getEnumLabel from 'app/utils/getEnumLabel'

export type LaneAwardRejectFilterRequestResponse = {
    id: number,
    changedOn: string,
    laneNumber: string,
    archived: boolean,
    pricingAccountCompanyName: string,
    accountCompanyName: string,
    accountIsStrategicAccount: boolean,
    accountId: number,
    originAirport: string,
    destinationAirport: string,
    customerExecution: string,
    customerExecutionProvisional: string,
    consignee: string,
    laneGroupId: number,
    quoteId: number,
    customerQuotation: string,
    handoverPointCountryName: string,
    handoverPointZipCode: string,
    collectionDropoffPointCountryName: string,
    collectionDropoffPointZipCode: string,
    expPalletsYear: number,
    frequency: Frequency,
    handoverPointLocationName: string,
    collectionDropoffPointLocationName: string,
    deliveryServiceType: DeliveryServiceType,
    collectionServiceType: CollectionServiceType,
    temperatureRange: TemperatureRange,
    leaseType: LeaseType,
    laneStatus: LaneStatus,
    changedBy: string,
    laneGroupNumber: string,
}

type LaneRequests = {
    domainName: string,
    filterRequestAllAccount: RequestConfigFunction<Record<string, any>>,
    get: RequestConfigFunction<{ id: number }>,
    getLaneAccountData: RequestConfigFunction<{ id: number }>,
    getRequestAwardedLaneProductTypes: RequestConfigFunction<{ id: number }>,
    updateByAccount: RequestConfigFunction<{ id: number }>,
    updateReduced: RequestConfigFunction<{ id: number }>,
    updatePriced: RequestConfigFunction<{ id: number }>,
    createByAccount: RequestConfigFunction,
    quote: RequestConfigFunction<{ id: number }>,
    assignToLaneGroup: RequestConfigFunction<{ id: number }>,
    updateAwardRejectLaneData: RequestConfigFunction<{ id: number }>,
    bulkCreate: RequestConfigFunction<{ data?: LaneBulkCreatePayload }>,
    archive: RequestConfigFunction<{ id: number }>,
    restore: RequestConfigFunction<{ id: number }>,
    lanesFilterRequestAwardReject: RequestConfigFunction<
        Record<string, any>, LaneAwardRejectFilterRequestResponse
    >,
    getLanesByQuote: RequestConfigFunction<Record<string, any>>,
    removeLaneFromLaneGroup: RequestConfigFunction<{ laneId: number }>,
    toBePriced: RequestConfigFunction<Record<string, any>>,
    // Available Lanes methods
    getAvailableLanes: RequestConfigFunction<Record<string, any>>,
    getAvailableLanesFilter: () => {
        customUrl: string
    },
    selector: (params) => SelectorConfig<LaneSelector[]>,
    duplicate: RequestConfigFunction<{ laneId: number }>,
    update: RequestConfigFunction<{ laneId: number }>,
    laneAllowedProductTypes: RequestConfigFunction<{
        laneId: number, leaseStartTimestamp: string,
        isPerformanceQualification: boolean,
        tempRange: DefaultTemperatureRange
    }>,
}

const laneRequests: LaneRequests = {
    // Account Lanes methods
    domainName: 'AccountLane',
    filterRequestAllAccount: ({
        params = {},
    } = {}) => {
        return {
            url: 'lane/filter-request/all-account',
            method: 'POST',
            ...params,
        }
    },
    get: ({
        id,
    }) => {
        return {
            url: `lane/${id}`,
            method: 'GET',
        }
    },
    getLaneAccountData: ({
        id,
    }) => {
        return {
            url: `lane/${id}/account-data`,
            method: 'GET',
        }
    },
    getRequestAwardedLaneProductTypes: ({
        id,
    }) => {
        return {
            url: `lane/${id}/producttype/requested-awarded`,
            method: 'GET',
        }
    },
    updateByAccount: ({
        id,
    }) => {
        return {
            url: `lane/${id}/account`,
            method: 'PATCH',
        }
    },
    updateReduced: ({
        id,
    }) => {
        return {
            url: `lane/${id}/account-reduced`,
            method: 'PATCH',
        }
    },
    updatePriced: ({
        id,
    }) => {
        return {
            url: `lane/${id}/account-inprogress-priced`,
            method: 'PATCH',
        }
    },
    createByAccount: () => {
        return {
            url: 'lane/by-account',
            method: 'POST',
        }
    },
    quote: ({
        id,
    }) => {
        return {
            url: `lane/${id}/quote`,
            method: 'PATCH',
        }
    },
    assignToLaneGroup: ({
        id,
    }) => {
        return {
            url: `lane/${id}/assignment-to-lanegroup`,
            method: 'PATCH',
        }
    },
    updateAwardRejectLaneData: ({
        id,
    }) => {
        return {
            url: `lane/${id}/awardreject-data`,
            method: 'PATCH',
        }
    },
    bulkCreate: ({
        data,
    } = {}) => {
        return {
            url: 'lane/by-account/bulk',
            data,
            method: 'POST',
        }
    },
    archive: ({
        id,
    }) => {
        return {
            url: `lane/${id}/archive`,
            method: 'PATCH',
        }
    },
    restore: ({
        id,
    }) => {
        return {
            url: `lane/${id}/restore`,
            method: 'PATCH',
        }
    },
    lanesFilterRequestAwardReject: (params = {}) => {
        return {
            url: 'lane/filter-request/awardreject',
            method: 'POST',
            ...params?.params,
            data: {
                ...params?.data,
            },
        }
    },
    getLanesByQuote: ({
        params = {},
    } = {}) => {
        return {
            url: 'lane/filter-request/quote-stepper',
            method: 'POST',
            ...params,
        }
    },
    removeLaneFromLaneGroup: ({
        laneId,
    }) => {
        return {
            url: `lane/${laneId}/unassignment-from-lanegroup`,
            method: 'PATCH',
        }
    },
    toBePriced: (params = {}) => {
        return {
            url: 'lane/filter-request/to-be-priced',
            method: 'POST',
            ...params,
        }
    },
    // Available Lanes methods
    getAvailableLanes: ({
        params,
    }) => {
        return {
            url: 'lane/filter-request/cs-overview',
            method: 'POST',
            ...params,
        }
    },
    getAvailableLanesFilter: () => {
        return {
            customUrl: 'lane/filter-request/cs-overview',
        }
    },
    selector: ({
        includeFilters,
        ...rest
    } = {}) => {
        return {
            url: 'lane/filter-request/selector',
            method: 'POST',
            convertion: (lanes) => {
                return lanes.map(({
                    laneNumber,
                    leaseType,
                    id,
                    customerExecutionLocationName,
                    handoverPointLocationName,
                    collectionDropoffPointLocationName,
                }) => {
                    return {
                        label: `${laneNumber} | ${getEnumLabel(leaseType)} | ${customerExecutionLocationName}`,
                        id,
                        value: id,
                        customerExecutionLocationName,
                        leaseType,
                        handoverPointLocationName,
                        collectionDropoffPointLocationName,
                    }
                })
            },
            ...rest,
            includeFilters,
        }
    },
    duplicate: (laneId) => {
        return {
            url: `lane/${laneId}/duplication`,
            method: 'POST',
        }
    },
    update: ({
        laneId,
    }: { laneId: number }) => {
        return {
            url: `lane/${laneId}`,
            method: 'PATCH',
        }
    },
    laneAllowedProductTypes: ({
        laneId,
        leaseStartTimestamp,
        isPerformanceQualification,
        tempRange,
    }) => {
        return {
            url: queryString.stringifyUrl({
                url: `lane/${laneId}/allowedproducttypes`,
                query: {
                    leaseStartTimestamp,
                    isPerformanceQualification,
                    tempRange,
                },
            }),
            method: 'GET',
        }
    },
}

export default laneRequests
